import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Auth0Client, User } from '@auth0/auth0-spa-js';
import { AuthContext } from './authContext';
import { hasAuthParams } from './hasAuthParams';
import { initialAuthState } from './authState';

export function AuthProvider({
  client,
  onRedirectCallback,
  children,
}: {
  client: Auth0Client;
  onRedirectCallback: () => void;
  children: ReactNode;
}) {
  const [error, setError] = useState<Error | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const initialized = useRef(false);
  useEffect(() => {
    if (initialized.current) {
      return;
    }
    initialized.current = true;
    (async (): Promise<void> => {
      try {
        let user: User | undefined;
        if (hasAuthParams()) {
          await client.handleRedirectCallback();
          user = await client.getUser();
          onRedirectCallback();
        } else {
          await client.checkSession();
          user = await client.getUser();
        }
        setIsAuthenticated(!!user);
      } catch (e: unknown) {
        if (e instanceof Error) {
          setError(e);
        }
      }
    })();
  }, [client, onRedirectCallback]);

  const login = useCallback(async () => {
    await client.loginWithRedirect({
      authorizationParams: {
        redirect_uri: `${window.location.origin}/home`,
      },
    });
  }, [client]);

  const logout = useCallback(async () => {
    await client.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }, [client]);

  return (
    <AuthContext.Provider value={{ ...initialAuthState, error, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
